import { FormKitNode } from '@formkit/core';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { SavePersonalDetails } from '~/composables/types/PersonalDetails';
import { defineStore } from 'pinia';
import { EnumAppointmentStatus, EnumAppointmentTypes, EnumServiceAreaExternalIntegrationType } from '~/composables/Enums';
import { Application, EmailReceived, PayeeInfos, SmsReceived } from '~/composables/types/Application';
import type {
  AssistanceRequestCustomForm,
  AssistanceRequestGeneratedFiles,
} from '~/composables/types/AssistanceRequest';
import { Fields } from '~/composables/types/AssistanceRequest';
import { Pagination, Sort } from '~/composables/types/Common';
import { ServiceAreaFilterView } from '~/composables/types/ServiceArea';
import { globalFetchOptions, sanitizeFileName } from '~/utils/helpers';
import { EnumAssistanceRequestStatus } from '~/composables/Enums';

interface UpdateStatusFormData {
  status: string;
  reason?: string;
  paymentMethod?: string;
  instructions?: string;
  declinedAdminReasonId?: string;
  declinedAdminReasonCustomText?: string;
}

export const useApplicationStore = defineStore('application', () => {
  const getById = (id: string) => {
    return $fetch<{ success: boolean; data: Application }>(`/applications/${id}`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const getConsentById = (id: string) => {
    return $fetch<{
      success: boolean;
      data: {
        id: string;
        blobID: string;
        fileName: string;
        remoteIP: string;
        contentType: string;
        createdAt: string;
      };
    }>(`/applications/${id}/consent`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  /**
   * PDFs generated for one Custom Form of the application (one per approved assistance request it applies
   * to), with how many are expected in total.
   */
  const getCustomFormFiles = (id: string, customFormId: string) => {
    return $fetch<{
      message: string | null;
      success: boolean;
      data: AssistanceRequestGeneratedFiles<AssistanceRequestCustomForm>;
    }>(`/applications/${id}/custom-forms/${customFormId}`, {
      ...globalFetchOptions(),
      method: 'get',
      // Fail soft: an error here only hides the optional Summary Documents section. Without the catch, a
      // 401 from this endpoint would log the admin out of the whole page.
    }).catch(() => null);
  };

  const edit = (id: string) => {
    return useMutation((formData: { stepId: string; data: unknown }) =>
      $fetch<SavePersonalDetails>(`/applications/${id}`, {
        ...globalFetchOptions(),
        method: 'PUT',
        body: formData,
        retry: false,
      })
    );
  };

  const showOnPrint = ref([
    {
      name: 'Applicant',
      value: true,
      stepId: 'applicant',
    },
    {
      name: 'Application',
      value: true,
      stepId: 'application',
    },
    {
      name: 'Additional Personal Information',
      value: true,
      stepId: '66539288-bb83-4be7-a723-8e3170a0e812',
    },
    {
      name: 'Additional Documentation',
      value: true,
      stepId: '140f7a0b-6fcb-4def-8f93-0860197a9ea1',
    },
    {
      name: 'Household Members',
      value: true,
      stepId: '4811a82e-0561-4ad6-9ea5-180a8ffed3d1',
    },
    {
      name: 'Health Insurance',
      value: true,
      stepId: '550689ed-ee32-4da8-a01e-9c879ae80976',
    },
    {
      name: 'Financial Information',
      value: true,
      stepId: '846e6870-81ce-49b3-87c8-21ca630ca752',
    },
    {
      name: 'Assistance Request (Electricity)',
      value: true,
      stepId: 'f933c0a3-8e4a-4f88-b195-c341d18c10dc',
      show: true,
    },
    {
      name: 'Assistance Request (Water)',
      value: true,
      stepId: 'dcaa09af-ee7f-40f7-9f18-645f6b1c55ee',
      show: true,
    },
    {
      name: 'Assistance Request (Gas/Propane)',
      value: true,
      stepId: '41e3acfb-9285-4ab0-b78a-1827dcddbb48',
      show: true,
    },
    {
      name: 'Assistance Request (Mortgage)',
      value: true,
      show: true,
      stepId: '2d8f7409-1cda-42c9-8c3c-b7ef6c8d4aef',
    },
    {
      name: 'Assistance Request (Rent)',
      value: true,
      stepId: '0c382324-31da-46bd-a886-a69c642f3d22',
      show: true,
    },
    {
      name: 'Women’s Shelter',
      stepId: '8e3f6978-f549-4cf4-a5ac-541f72c4cca5',
      value: true,
      show: true,
    },
    {
      name: 'Men’s Shelter',
      stepId: 'f83cc8ac-83eb-45a2-bd83-b8f2e3525e8b',
      value: true,
      show: true,
    },
    {
      name: 'Women’s and Children Shelter',
      stepId: '70f6f171-3d47-4cfe-979e-5da330b60228',
      value: true,
      show: true,
    },
    {
      name: 'Family Shelter',
      stepId: '7c3656d3-3dd4-4d10-84ce-9b35f98318a4',
      value: true,
      show: true,
    },
    {
      name: 'Food Pantry',
      stepId: 'b7dff532-d226-45ce-8744-4915703c1284',
      value: true,
      show: true,
    },
    {
      name: 'Soup Kitchen',
      stepId: '30b753c9-3b30-4d04-874a-ddab7745ecb4',
      value: true,
      show: true,
    },
    {
      name: 'Bus Ticket',
      stepId: '7ef55d90-bd59-425f-a850-cef71996ecde',
      value: true,
      show: true,
    },
    {
      name: 'Disaster Event',
      stepId: '7aa94f58-4ad6-47d8-9488-fca5589d7fbd',
      value: true,
      show: true,
    },
    {
      name: 'Clothing Voucher',
      stepId: '641e9bba-38ee-4703-aad8-186cd05cdabf',
      value: true,
      show: true,
    },
    {
      name: 'Appliance Repair/Purchase',
      stepId: '51e92006-9827-4a67-97da-847147b580db',
      value: true,
      show: true,
    },
    {
      name: 'Electricity',
      stepId: '6aa33c24-9697-4486-bf13-12ab07775fc0',
      value: true,
      show: true,
    },
    {
      name: 'Food',
      stepId: '61aac241-f127-435e-b286-569b6ea745f3',
      value: true,
      show: true,
    },
    {
      name: 'Water',
      stepId: '592bd8b2-a98e-48fb-a516-1a416555eccc',
      value: true,
      show: true,
    },
    {
      name: 'Gas/Propane',
      stepId: 'c4dd875d-7227-4d86-9a30-b380d7c73aab',
      value: true,
      show: true,
    },
    {
      name: 'FPL Saving Steps',
      value: true,
      stepId: '5f697a9f-b59d-483a-b19c-f4ef8749419e',
      show: true,
    },
    {
      name: 'Home Repair',
      stepId: '929ff865-dc5b-489b-9504-ace6603ca333',
      value: true,
      show: true,
    },
    {
      name: 'Temporary Lodging',
      stepId: 'fbf1c284-5949-4532-8f42-d0324c07b834',
      value: true,
      show: true,
    },
    {
      name: 'Vehicle Repair',
      stepId: '147e15f9-ca37-4fcd-ba9a-c608c9c4505c',
      value: true,
      show: true,
    },
    {
      name: 'Mortgage',
      stepId: '0315faa4-e7eb-43ed-aad1-b73da8d2f5d7',
      value: true,
      show: true,
    },
    {
      name: 'Vehicle Repair',
      stepId: '59022cbd-dd6f-424c-8339-f53967f628f7',
      value: true,
      show: true,
    },
    {
      name: 'Gas Money',
      stepId: '7c9433ae-13c9-4c89-a42d-452dc09059e8',
      value: true,
      show: true,
    },
    {
      name: 'Rent',
      stepId: '00f04179-6bec-4ed2-b801-0e9f289ebae5',
      value: true,
      show: true,
    },
    {
      name: 'Gas Money',
      value: true,
      stepId: '1aa8895a-7f5b-4224-ba79-d50440ecec46',
      show: true,
    },
    {
      name: 'Meter Repair (Electricity)',
      value: true,
      stepId: '998fd5cd-e4ae-499d-aff8-99145c9d01d5',
      show: true,
    },
    {
      name: 'Meter Repair (Electricity)',
      value: true,
      stepId: 'f8a514f4-8c30-4cf7-959c-db94851f050f',
      show: true,
    },
    {
      name: 'FPL Saving Steps',
      value: true,
      stepId: '5f697a9f-b59d-483a-b19c-f4ef8749419e',
      show: true,
    },
    {
      name: 'Gas Money',
      value: true,
      stepId: '1aa8895a-7f5b-4224-ba79-d50440ecec46',
      show: true,
    },
    {
      name: 'Meter Repair (Electricity)',
      value: true,
      stepId: '998fd5cd-e4ae-499d-aff8-99145c9d01d5',
      show: true,
    },
    {
      name: 'Meter Repair (Electricity)',
      value: true,
      stepId: '998fd5cd-e4ae-499d-aff8-99145c9d01d5',
      show: true,
    },
    {
      name: 'Meter Repair (Electricity)',
      value: true,
      stepId: 'f8a514f4-8c30-4cf7-959c-db94851f050f',
      show: true,
    },
    {
      name: 'Meter Repair (Electricity)',
      value: true,
      stepId: 'f8a514f4-8c30-4cf7-959c-db94851f050f',
      show: true,
    },
    {
      name: 'Temporary Lodging',
      value: true,
      stepId: 'fbf1c284-5949-4532-8f42-d0324c07b834',
      show: true,
    },
    {
      name: 'Consent',
      value: true,
      stepId: '846e6870-81ce-49b3-87c8-21ca630ca733',
    },
    {
      name: 'Supporting Documents',
      stepId: 'supporting-documents',
      value: true,
    },
    {
      name: 'Comment/History',
      stepId: 'comment-history',
      value: true,
    },
    {
      name: 'Appointments',
      stepId: 'appointments',
      value: true,
    },
    {
      name: 'Emails Sent',
      stepId: 'email-sent',
      value: true,
    },
    {
      name: 'Text Sent',
      stepId: 'text-sent',
      value: true,
    },
    {
      name: 'Video Session',
      stepId: 'video-session',
      value: true,
    },
    {
      name: 'Chat',
      stepId: 'chat',
      value: true,
    },
  ]);

  const uploadDocument = (id: string, formData: FormData) => {
    return $fetch<{ success: boolean; data: string }>(`/applications/${id}/file`, {
      ...globalFetchOptions(),
      method: 'POST',
      body: formData,
      retry: false,
      onResponseError: (error: any) => {
        toastError(
          '',
          'There is an issue with the file you are trying to upload. Please confirm your file is valid and retry your upload.'
        );
      },
    });
  };

  const getDocument = (
    appId: string,
    fileId: string,
    fileName?: string,
    fileDescription?: string,
    isFromPreview = false
  ) => {
    const fName = fileName ? `&fileName=${encodeURIComponent(sanitizeFileName(fileName))}` : '';
    const fDesc = fName && fileDescription ? `&fileDescription=${encodeURIComponent(fileDescription)}` : '';
    return $fetch<Blob>(`/applications/${appId}/file/${fileId}?isFromPreview=${isFromPreview}${fName + fDesc}`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  const getSasDocument = (
    appId: string,
    fileId: string,
    fileName?: string,
    fileDescription?: string,
    isFromPreview = false
  ) => {
    const fName = fileName ? `&filename=${encodeURIComponent(sanitizeFileName(fileName))}` : '';
    const fDesc = fName && fileDescription ? `&fileDescription=${encodeURIComponent(fileDescription)}` : '';
    return $fetch<Blob>(`/applications/${appId}/file/${fileId}/sas?isFromPreview=${isFromPreview}${fName + fDesc}`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  const getVideoRecordingUrl = (meetingId: string) => {
    return $fetch<{ success: boolean; data: string }>(`/meetings/${meetingId}/recording`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  const getSignature = (fileId: string) => {
    return $fetch<Blob>(`/files/${fileId}`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
      onResponseError: (error: any) => {
        toastError(
          '',
          'There is an issue with the file you are trying to upload. Please confirm your file is valid and retry your upload.'
        );
      },
    });
  };

  const getHistory = (id: string) => {
    return $fetch<{ success: boolean; data: unknown }>(`/applications/${id}/history`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const getAppointmentHistory = (id: string) => {
    return $fetch<{ success: boolean; data: unknown }>(`/applications/${id}/appointment-history`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const getEmailReceived = (id: string, pagination: Pagination, sort: Sort) => {
    return $fetch<{
      success: boolean;
      data: {
        result: EmailReceived[];
      };
    }>(`/applications/${id}/emails-received?sort=${sort.id}&order=${sort.desc ? 'desc' : 'asc'}`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const getAppointment = (id: string, pagination: Pagination, sort: Sort) => {
    return {
      success: true,
      data: {
        result: [
          {
            id: '45ee9856-97d0-47dc-be47-d030c2fec541',
            date: '2023-08-30',
            startTime: '23:02',
            endTime: '23:52',
            type: EnumAppointmentTypes.Phone,
            caseWorker: 'Jeff Marquis',
            status: EnumAppointmentStatus.Requested,
            notes: '',
          },
          {
            id: '45ee9856-97d0-47dc-be47-d030c2faf8975',
            date: '2023-08-30',
            startTime: '23:02',
            endTime: '23:52',
            type: EnumAppointmentTypes.InPerson,
            caseWorker: 'Jeff Marquis',
            status: EnumAppointmentStatus.Requested,
            notes: '',
          },
        ],
        count: 2,
        pages: 1,
      },
    };
  };

  const resendEmail = (appId: string, emailId: string) => {
    return useMutation((formData: { emailTo: string; tableReference: string }) => {
      return $fetch<SavePersonalDetails>(`/applications/${appId}/emails-received/resend`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: {
          id: emailId,
          destinationEmail: formData.emailTo,
          tableReference: formData.tableReference,
        },
        retry: false,
      });
    });
  };

  const getSmsReceived = (id: string, pagination: Pagination, sort: Sort) => {
    return $fetch<{
      success: boolean;
      data: { result: SmsReceived[] };
    }>(`/applications/${id}/sms-received?sort=${sort.id}&order=${sort.desc ? 'desc' : 'asc'}`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const resendSms = (appId: string, smsId: number) => {
    return useMutation((formData: { destinationNumber: string; tableReference: string }) => {
      return $fetch<SavePersonalDetails>(`/applications/${appId}/sms-received/resend`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: {
          id: smsId,
          destinationNumber: formData.destinationNumber,
          tableReference: formData.tableReference,
        },
        retry: false,
      });
    });
  };

  const getVideoSessions = (id: string, pagination: Pagination, sort: Sort) => {
    return $fetch<{
      success: boolean;
      data: { result: SmsReceived[] };
    }>(`/meetings?applicationId=${id}&sort=${sort.id}&order=${sort.desc ? 'desc' : 'asc'}`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const getPayeeEmailSent = (id: string) => {
    return {
      success: true,
      data: {
        result: [
          {
            id: '4af76cfe-6f7c-415d-2d6e-08db9cb51906',
            subject: '[DEV Emailer] SAHelp Application Status: Submitted',
            destinationEmail: 'tsamm-internal@dataparadigm.com',
            sourceEmail: 'noreply@tsamm.org',
            sourceRealName: 'SAHelp',
            body: 'Original Dest Email: taoufiq.benmessaoud@gmail.com\n\r<!doctype html>\n<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">\n<head>\n    <!-- NAME: 1 COLUMN -->\n    <!--[if gte mso 15]>\n    <xml>\n        <o:OfficeDocumentSettings>\n        <o:AllowPNG/>\n        <o:PixelsPerInch>96</o:PixelsPerInch>\n        </o:OfficeDocumentSettings>\n    </xml>\n    <![endif]-->\n    <meta charset="UTF-8">\n    <meta http-equiv="X-UA-Compatible" content="IE=edge">\n    <meta name="viewport" content="width=device-width, initial-scale=1">\n    <title>Application Submitted</title>\n\n    <style type="text/css">\n        p {\n            margin: 10px 0;\n            padding: 0;\n        }\n\n        table {\n            border-collapse: collapse;\n        }\n\n        h1, h2, h3, h4, h5, h6 {\n            display: block;\n            margin: 0;\n            padding: 0;\n        }\n\n        img, a img {\n            border: 0;\n            height: auto;\n            outline: none;\n            text-decoration: none;\n        }\n\n        body, #bodyTable, #bodyCell {\n            height: 100%;\n            margin: 0;\n            padding: 0;\n            width: 100%;\n        }\n\n        .mcnPreviewText {\n            display: none !important;\n        }\n\n        #outlook a {\n            padding: 0;\n        }\n\n        img {\n            -ms-interpolation-mode: bicubic;\n        }\n\n        table {\n            mso-table-lspace: 0pt;\n            mso-table-rspace: 0pt;\n        }\n\n        .ReadMsgBody {\n            width: 100%;\n        }\n\n        .ExternalClass {\n            width: 100%;\n        }\n\n        p, a, li, td, blockquote {\n            mso-line-height-rule: exactly;\n        }\n\n            a[href^=tel], a[href^=sms] {\n                color: inherit;\n                cursor: default;\n                text-decoration: none;\n            }\n\n        p, a, li, td, body, table, blockquote {\n            -ms-text-size-adjust: 100%;\n            -webkit-text-size-adjust: 100%;\n        }\n\n        .ExternalClass, .ExternalClass p, .ExternalClass td, .ExternalClass div, .ExternalClass span, .ExternalClass font {\n            line-height: 100%;\n        }\n\n        a[x-apple-data-detectors] {\n            color: inherit !important;\n            text-decoration: none !important;\n            font-size: inherit !important;\n            font-family: inherit !important;\n            font-weight: inherit !important;\n            line-height: inherit !important;\n        }\n\n        #bodyCell {\n            padding: 10px;\n        }\n\n        .templateContainer {\n            max-width: 600px !important;\n        }\n\n        a.mcnButton {\n            display: block;\n        }\n\n        .mcnImage, .mcnRetinaImage {\n            vertical-align: bottom;\n        }\n\n        .mcnTextContent {\n            word-break: break-word;\n        }\n\n            .mcnTextContent img {\n                height: auto !important;\n            }\n\n        .mcnDividerBlock {\n            table-layout: fixed !important;\n        }\n\n        body, #bodyTable {\n            background-color: #FAFAFA;\n        }\n\n        #bodyCell {\n            border-top: 0;\n        }\n\n        .templateContainer {\n            border: 0;\n        }\n\n        h1 {\n            color: #202020;\n            font-family: Helvetica;\n            font-size: 26px;\n            font-style: normal;\n            font-weight: bold;\n            line-height: 125%;\n            letter-spacing: normal;\n            text-align: left;\n        }\n\n        h2 {\n            color: #202020;\n            font-family: Helvetica;\n            font-size: 22px;\n            font-style: normal;\n            font-weight: bold;\n            line-height: 125%;\n            letter-spacing: normal;\n            text-align: left;\n        }\n\n        h3 {\n            color: #202020;\n            font-family: Helvetica;\n            font-size: 20px;\n            font-style: normal;\n            font-weight: bold;\n            line-height: 125%;\n            letter-spacing: normal;\n            text-align: left;\n        }\n\n        h4 {\n            color: #202020;\n            font-family: Helvetica;\n            font-size: 18px;\n            font-style: normal;\n            font-weight: bold;\n            line-height: 125%;\n            letter-spacing: normal;\n            text-align: left;\n        }\n\n        #templatePreheader {\n            background-color: #FAFAFA;\n            background-image: none;\n            background-repeat: no-repeat;\n            background-position: center;\n            background-size: cover;\n            border-top: 0;\n            border-bottom: 0;\n            padding-top: 9px;\n            padding-bottom: 9px;\n        }\n\n            #templatePreheader .mcnTextContent, #templatePreheader .mcnTextContent p {\n                color: #656565;\n                font-family: Helvetica;\n                font-size: 12px;\n                line-height: 150%;\n                text-align: left;\n            }\n\n                #templatePreheader .mcnTextContent a, #templatePreheader .mcnTextContent p a {\n                    color: #656565;\n                    font-weight: normal;\n                    text-decoration: underline;\n                }\n\n        #templateHeader {\n            background-color: #FFFFFF;\n            background-image: none;\n            background-repeat: no-repeat;\n            background-position: center;\n            background-size: cover;\n            border-top: 0;\n            border-bottom: 0;\n            padding-top: 9px;\n            padding-bottom: 0;\n        }\n\n            #templateHeader .mcnTextContent, #templateHeader .mcnTextContent p {\n                color: #202020;\n                font-family: Helvetica;\n                font-size: 16px;\n                line-height: 150%;\n                text-align: left;\n            }\n\n                #templateHeader .mcnTextContent a, #templateHeader .mcnTextContent p a {\n                    color: #007C89;\n                    font-weight: normal;\n                    text-decoration: underline;\n                }\n\n        #templateBody {\n            background-color: #FFFFFF;\n            background-image: none;\n            background-repeat: no-repeat;\n            background-position: center;\n            background-size: cover;\n            border-top: 0;\n            border-bottom: 2px solid #EAEAEA;\n            padding-top: 0;\n            padding-bottom: 9px;\n        }\n\n            #templateBody .mcnTextContent, #templateBody .mcnTextContent p {\n                color: #202020;\n                font-family: Helvetica;\n                font-size: 16px;\n                line-height: 150%;\n                text-align: left;\n            }\n\n                #templateBody .mcnTextContent a, #templateBody .mcnTextContent p a {\n                    color: #007C89;\n                    font-weight: normal;\n                    text-decoration: underline;\n                }\n\n        #templateFooter {\n            background-color: #FAFAFA;\n            background-image: none;\n            background-repeat: no-repeat;\n            background-position: center;\n            background-size: cover;\n            border-top: 0;\n            border-bottom: 0;\n            padding-top: 9px;\n            padding-bottom: 9px;\n        }\n\n            #templateFooter .mcnTextContent, #templateFooter .mcnTextContent p {\n                color: #656565;\n                font-family: Helvetica;\n                font-size: 12px;\n                line-height: 150%;\n                text-align: center;\n            }\n\n                #templateFooter .mcnTextContent a, #templateFooter .mcnTextContent p a {\n                    color: #656565;\n                    font-weight: normal;\n                    text-decoration: underline;\n                }\n\n        @media only screen and (min-width:768px) {\n            .templateContainer {\n                width: 600px !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            body, table, td, p, a, li, blockquote {\n                -webkit-text-size-adjust: none !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            body {\n                width: 100% !important;\n                min-width: 100% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnRetinaImage {\n                max-width: 100% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnImage {\n                width: 100% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnCartContainer, .mcnCaptionTopContent, .mcnRecContentContainer, .mcnCaptionBottomContent, .mcnTextContentContainer, .mcnBoxedTextContentContainer, .mcnImageGroupContentContainer, .mcnCaptionLeftTextContentContainer, .mcnCaptionRightTextContentContainer, .mcnCaptionLeftImageContentContainer, .mcnCaptionRightImageContentContainer, .mcnImageCardLeftTextContentContainer, .mcnImageCardRightTextContentContainer, .mcnImageCardLeftImageContentContainer, .mcnImageCardRightImageContentContainer {\n                max-width: 100% !important;\n                width: 100% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnBoxedTextContentContainer {\n                min-width: 100% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnImageGroupContent {\n                padding: 9px !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnCaptionLeftContentOuter .mcnTextContent, .mcnCaptionRightContentOuter .mcnTextContent {\n                padding-top: 9px !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnImageCardTopImageContent, .mcnCaptionBottomContent:last-child .mcnCaptionBottomImageContent, .mcnCaptionBlockInner .mcnCaptionTopContent:last-child .mcnTextContent {\n                padding-top: 18px !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnImageCardBottomImageContent {\n                padding-bottom: 9px !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnImageGroupBlockInner {\n                padding-top: 0 !important;\n                padding-bottom: 0 !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnImageGroupBlockOuter {\n                padding-top: 9px !important;\n                padding-bottom: 9px !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnTextContent, .mcnBoxedTextContentColumn {\n                padding-right: 18px !important;\n                padding-left: 18px !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcnImageCardLeftImageContent, .mcnImageCardRightImageContent {\n                padding-right: 18px !important;\n                padding-bottom: 0 !important;\n                padding-left: 18px !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            .mcpreview-image-uploader {\n                display: none !important;\n                width: 100% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            h1 {\n                font-size: 22px !important;\n                line-height: 125% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            h2 {\n                font-size: 20px !important;\n                line-height: 125% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            h3 {\n                font-size: 18px !important;\n                line-height: 125% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            h4 {\n                font-size: 16px !important;\n                line-height: 150% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n\n            .mcnBoxedTextContentContainer .mcnTextContent, .mcnBoxedTextContentContainer .mcnTextContent p {\n                font-size: 14px !important;\n                line-height: 150% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n\n            #templatePreheader {\n                display: block !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            #templatePreheader .mcnTextContent, #templatePreheader .mcnTextContent p {\n                font-size: 14px !important;\n                line-height: 150% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            #templateHeader .mcnTextContent, #templateHeader .mcnTextContent p {\n                font-size: 16px !important;\n                line-height: 150% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n\n            #templateBody .mcnTextContent, #templateBody .mcnTextContent p {\n                font-size: 16px !important;\n                line-height: 150% !important;\n            }\n        }\n\n        @media only screen and (max-width: 480px) {\n            #templateFooter .mcnTextContent, #templateFooter .mcnTextContent p {\n                font-size: 14px !important;\n                line-height: 150% !important;\n            }\n        }\n    </style>\n</head>\n<body>\n    <!--*|IF:MC_PREVIEW_TEXT|*-->\n    <!--[if !gte mso 9]><!---->\n    <span class="mcnPreviewText" style="display:none; font-size:0px; line-height:0px; max-height:0px; max-width:0px; opacity:0; overflow:hidden; visibility:hidden; mso-hide:all;"></span><!--<![endif]-->\n    <!--*|END:IF|*-->\n    <center>\n        <table align="center" border="0" cellpadding="0" cellspacing="0" height="100%" width="100%" id="bodyTable">\n            <tr>\n                <td align="center" valign="top" id="bodyCell">\n                    <!-- BEGIN TEMPLATE // -->\n                    <!--[if (gte mso 9)|(IE)]>\n                    <table align="center" border="0" cellspacing="0" cellpadding="0" width="600" style="width:600px;">\n                    <tr>\n                    <td align="center" valign="top" width="600" style="width:600px;">\n                    <![endif]-->\n                    <table border="0" cellpadding="0" cellspacing="0" width="100%" class="templateContainer">\n                        <tr>\n                            <td valign="top" id="templatePreheader"></td>\n                        </tr>\n                        <tr>\n                            <td valign="top" id="templateHeader">\n                                <table border="0" cellpadding="0" cellspacing="0" width="100%" class="mcnImageBlock" style="min-width:100%;">\n                                    <tbody class="mcnImageBlockOuter">\n                                        <tr>\n                                            <td valign="top" style="padding:9px" class="mcnImageBlockInner">\n                                                <table align="left" width="100%" border="0" cellpadding="0" cellspacing="0" class="mcnImageContentContainer" style="min-width:100%;">\n                                                    <tbody>\n                                                        <tr>\n                                                            <td class="mcnImageContent" valign="top" style="padding-right: 9px; padding-left: 9px; padding-top: 0; padding-bottom: 0; text-align:center;">\n                                                                <img align="center" alt="" src="https://mcusercontent.com/13293d1003670dcaa34c5b88d/images/8ff98336-ef75-477f-9dfc-b7a518ea1ca0.png" width="169.2" style="max-width:1200px; padding-bottom: 0; display: inline !important; vertical-align: bottom;" class="mcnImage">\n                                                            </td>\n                                                        </tr>\n                                                    </tbody>\n                                                </table>\n                                            </td>\n                                        </tr>\n                                    </tbody>\n                                </table>\n                            </td>\n                        </tr>\n                        <tr>\n                            <td valign="top" id="templateBody">\n                                <table border="0" cellpadding="0" cellspacing="0" width="100%" class="mcnTextBlock" style="min-width:100%;">\n                                    <tbody class="mcnTextBlockOuter">\n                                        <tr>\n                                            <td valign="top" class="mcnTextBlockInner" style="padding-top:9px;">\n                                                <!--[if mso]>\n                                                <table align="left" border="0" cellspacing="0" cellpadding="0" width="100%" style="width:100%;">\n                                                <tr>\n                                                <![endif]-->\n                                                <!--[if mso]>\n                                                <td valign="top" width="600" style="width:600px;">\n                                                <![endif]-->\n                                                <div>\n                                                    <table align="left" border="0" cellpadding="0" cellspacing="0" style="max-width:100%; min-width:100%;" width="100%" class="mcnTextContentContainer">\n    <tbody>\n        <tr>\n\n            <td valign="top" class="mcnTextContent" style="padding: 0px 18px 9px; font-size: 12px;">\n\n                <p style="font-size: 12px;">\n                    <br>\n                    SAHelp: Your application for the following assistance type(s) has been received. An SAHelp representative will review your request and reach out if they require any additional information.\n                    <br>\n                    <div style="text-indent: -2em; padding-left: 2em;">\n    <strong>Assistance Type(s) applied for: </strong>\n    <br>\n    <br>\nMortgage            <br>\n</div>\n                </p>\n                <p style="font-size: 12px;">\n                    <br>\n                    Please contact your local Salvation Army if you need to alter or update your assistance request.\n                    <br><br>\n                </p>\n\n            </td>\n        </tr>\n    </tbody>\n</table>\n\n                                                </div>\n                                                <!--[if mso]>\n                                                </td>\n                                                <![endif]-->\n                                                <!--[if mso]>\n                                                </tr>\n                                                </table>\n                                                <![endif]-->\n                                            </td>\n                                        </tr>\n                                    </tbody>\n                                </table>\n                            </td>\n                        </tr>\n                        <tr>\n                            <td valign="top" id="templateFooter">\n                                <table border="0" cellpadding="0" cellspacing="0" width="100%" class="mcnDividerBlock" style="min-width:100%;">\n                                    <tbody class="mcnDividerBlockOuter">\n                                        <tr>\n                                            <td class="mcnDividerBlockInner" style="min-width: 100%; padding: 10px 18px 25px;">\n                                                <table class="mcnDividerContent" border="0" cellpadding="0" cellspacing="0" width="100%" style="min-width: 100%;border-top-width: 2px;border-top-style: solid;border-top-color: #EEEEEE;">\n                                                    <tbody>\n                                                        <tr>\n                                                            <td>\n                                                                <span></span>\n                                                            </td>\n                                                        </tr>\n                                                    </tbody>\n                                                </table>\n                                            </td>\n                                        </tr>\n                                    </tbody>\n                                </table><table border="0" cellpadding="0" cellspacing="0" width="100%" class="mcnTextBlock" style="min-width:100%;">\n                                    <tbody class="mcnTextBlockOuter">\n                                        <tr>\n                                            <td valign="top" class="mcnTextBlockInner" style="padding-top:9px;">\n                                                <!--[if mso]>\n                                                <table align="left" border="0" cellspacing="0" cellpadding="0" width="100%" style="width:100%;">\n                                                <tr>\n                                                <![endif]-->\n                                                <!--[if mso]>\n                                                <td valign="top" width="600" style="width:600px;">\n                                                <![endif]-->\n                                                <table align="left" border="0" cellpadding="0" cellspacing="0" style="max-width:100%; min-width:100%;" width="100%" class="mcnTextContentContainer">\n                                                    <tbody>\n                                                        <tr>\n                                                            <td valign="top" class="mcnTextContent" style="padding: 0px 18px 9px; text-align: left;">\n                                                                <span style="color:#767E9D">\n                                                                    This email is intended to notify SAHelp applicants about developments relating to their requests. \n                                                                    If you have received this message in error, or if you would like to stop receiving this and other similar communications from SAHelp,\n                                                                    please <a href="https://dev.www.tsamm.org/SAHelpOptout.dpi?ee=0xCC80E54F81415A6C698180F2F11FF56C">CLICK HERE</a>.\n                                                                    <br>\n                                                                    <br>\n                                                                    Please do not reply to this email, as the address is part of an automated system. Emails sent to this address are not monitored.\n                                                                </span>\n                                                            </td>\n                                                        </tr>\n                                                    </tbody>\n                                                </table>\n                                                <!--[if mso]>\n                                                </td>\n                                                <![endif]-->\n                                                \n                                                <!--[if mso]>\n                                                </tr>\n                                                </table>\n                                                <![endif]-->\n                                            </td>\n                                        </tr>\n                                    </tbody>\n                                </table>\n                            </td>\n                        </tr>\n                    </table>\n                    <!--[if (gte mso 9)|(IE)]>\n                    </td>\n                    </tr>\n                    </table>\n                    <![endif]-->\n                    <!-- // END TEMPLATE -->\n                </td>\n            </tr>\n        </table>\n    </center>\n</body>\n</html>\n\n',
            externalId: 'b9727b29-876e-4a00-87e7-fb26b46d834c',
            created: '2023-08-14T05:58:48.723',
            sent: '2023-08-14T05:58:50.293',
            tableReference: 'tblEmailer',
          },
        ],
      },
    };
  };

  const getPayeeInformation = (id: string) => {
    return $fetch<{
      success: boolean;
      data: {
        result: PayeeInfos[];
      };
    }>(`/payee/${id}/submissions`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const getPayeeConsentById = (id: string) => {
    return $fetch<{
      success: boolean;
      data: {
        id: string;
        blobID: string;
        fileName: string;
        remoteIP: string;
        contentType: string;
        createdAt: string;
      };
    }>(`/payee/${id}/consent`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const resendPayeeEmail = (appId: string, emailId: string) => {
    return useMutation((formData: { emailTo: string; tableReference: string }) => {
      return $fetch<SavePersonalDetails>(`/assistance-request/${appId}/payee-emails-sent/resend`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: {
          id: emailId,
          destinationEmail: formData.emailTo,
          tableReference: formData.tableReference,
        },
        retry: false,
      });
    });
  };

  const getComments = (id: string) => {
    return $fetch<{ success: boolean; data: unknown }>(`/applications/${id}/comments`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  /** @param prefix prepended to the message — pass '' for system-written entries that carry their own wording */
  const addComment = (id: string, prefix = 'Added Comment: ') => {
    return useMutation((formData: { message: string }) => {
      formData.message = prefix + formData.message;
      return $fetch<SavePersonalDetails>(`/applications/${id}/comments`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: formData,
        retry: false,
      });
    });
  };

  const updateChatFlag = (id: string) => {
    return useMutation((formData: { chat: boolean }) => {
      return $fetch<{ success: boolean }>(`/applications/${id}/action-flags/Chat/${formData.chat}`, {
        ...globalFetchOptions(),
        method: 'PUT',
        retry: false,
      });
    });
  };

  const updateArFlag = (id: string) => {
    return useMutation((formData: { payee: boolean }) => {
      return $fetch<{ success: boolean }>(`/assistance-requests/${id}/action-flags/Payee/${formData.payee}`, {
        ...globalFetchOptions(),
        method: 'PUT',
        retry: false,
      });
    });
  };

  const updateStatus = (id: string) => {
    return useMutation((formData: UpdateStatusFormData) => {
      const body = {
        reason: formData.reason ?? '',
        paymentMethod: formData.paymentMethod ?? '',
        paymentInstructions: formData.instructions ?? '',
        declinedAdminReasonId: formData.declinedAdminReasonId ?? null,
        declinedAdminReasonCustomText: formData.declinedAdminReasonCustomText ?? null,
      };

      return $fetch<SavePersonalDetails>(`/applications/${id}/status/${formData.status}`, {
        ...globalFetchOptions(),
        method: 'PUT',
        retry: false,
        body,
      });
    });
  };

  const statusToPermission: Map<string, number[]> = new Map([
    [EnumAssistanceRequestStatus.Accepted, [TsammPermission.SAHelpSupport]],
    [EnumAssistanceRequestStatus.ApprovalPending, [TsammPermission.SAHelpCaseManagement]],
    [EnumAssistanceRequestStatus.Approved, [TsammPermission.SAHelpCaseManagementDirector]],
    [EnumAssistanceRequestStatus.ApprovedReceivedByFinance, [TsammPermission.SAHelpPaymentProcessing]],
    [EnumAssistanceRequestStatus.ApprovedFundingSent, [TsammPermission.SAHelpPaymentProcessing]],
    [EnumAssistanceRequestStatus.ApprovedClosed, [TsammPermission.SAHelpCaseManagement]],
    [EnumAssistanceRequestStatus.Declined, [TsammPermission.SAHelpSupport, TsammPermission.SAHelpCaseManagement]],
    [EnumAssistanceRequestStatus.ReturnedToApplicant, [TsammPermission.SAHelpSupport, TsammPermission.SAHelpCaseManagement]],
    [EnumAssistanceRequestStatus.Rescinded, [TsammPermission.SAHelpCaseManagementDirector]],
  ]);

  const getValidNextStatuses = (currentStatus: string | undefined): string[] => {
    switch (currentStatus) {
      case EnumAssistanceRequestStatus.Submitted:
        return [
          EnumAssistanceRequestStatus.Accepted,
          EnumAssistanceRequestStatus.ReturnedToApplicant,
          EnumAssistanceRequestStatus.Declined,
        ];

      default:
        return [];
    }
  };

  const getDisplayStringForStatusUpdateButtons = (status: string, isFood = false): string => {
    switch (status) {
      case EnumAssistanceRequestStatus.Approved:
        return 'Approve';

      case EnumAssistanceRequestStatus.Declined:
        return 'Decline';

      case EnumAssistanceRequestStatus.ReturnedToApplicant:
        return 'Return to the Applicant';

      case EnumAssistanceRequestStatus.Accepted:
        return 'Accept';

      case EnumAssistanceRequestStatus.ApprovalPending:
        return 'Submit for Approval';

      case EnumAssistanceRequestStatus.ApprovedClosed:
        return isFood ? 'Mark as Closed' : 'Finish and Close';

      case EnumAssistanceRequestStatus.Rescinded:
        return 'Rescind';

      case EnumAssistanceRequestStatus.ApprovedReceivedByFinance:
        return 'Mark as Received by Finance';

      case EnumAssistanceRequestStatus.ApprovedFundingSent:
        return 'Mark as Payment Sent';

      default:
        throw new Error(`Invalid status: ${status}`);
    }
  };

  const getChatHistory = (id: string, page = 1, pageSize = 1000) => {
    return $fetch<{ success: boolean; data: unknown }>(
      `/applications/${id}/chat-history?sort=createdAt&order=asc&page=${page}&pageSize=${pageSize}`,
      {
        ...globalFetchOptions(),
        method: 'get',
      }
    );
  };

  const getUnreadChatMessage = (id: string) => {
    return $fetch<{ success: boolean; data: unknown }>(`/applications/${id}/chat/count/unread`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const integrationEnabled = ref(false);
  const setIntegrationEnabled = (value: boolean) => {
    integrationEnabled.value = value;
  };

  const wellskyActive = ref(false);
  const setWellskyActive = (value: boolean) => {
    wellskyActive.value = value;
  };
  const checkIntegrationEnabled = () => {
    return $fetch<{ success: boolean; data: any }>(`/integrations/wellsky/enabled`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const activeWellsky = ref({
    type: EnumServiceAreaExternalIntegrationType.Disabled,
    canPerformCommunityServicesMatch: false,
  });
  const setActiveWellsky = (data: {
    type: EnumServiceAreaExternalIntegrationType;
    canPerformCommunityServicesMatch: boolean;
  }) => {
    activeWellsky.value = data;
  };

  const checkActiveWellsky = (id: string) => {
    return $fetch<{ success: boolean; data: any }>(`/applications/${id}/integration/wellsky`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const chatNotifyInit = (id: string) => {
    return $fetch<{ success: boolean; data: unknown }>(`/applications/${id}/chat/notify-initiation`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const addChatText = (id: string) => {
    return useMutation((formData: { message: string }) =>
      $fetch<SavePersonalDetails>(`/applications/${id}/chat/text`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: formData,
        retry: false,
      })
    );
  };

  const addChatFile = (id: string) => {
    return useMutation((formData: FormData) =>
      $fetch<SavePersonalDetails>(`/applications/${id}/chat/file`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: formData,
        retry: false,
        onResponseError: (error: any) => {
          toastError(
            '',
            'There is an issue with the file you are trying to upload. Please confirm your file is valid and retry your upload.'
          );
        },
      })
    );
  };

  const deleteChatMessage = (id: string) => {
    return useMutation((formData: { messageId: string }) =>
      $fetch<{ success: boolean }>(`/applications/${id}/chat/${formData.messageId}/delete`, {
        ...globalFetchOptions(),
        method: 'POST',
        retry: false,
      })
    );
  };

  const downloadChatFile = async (arId: string, fileId: string, name: string) => {
    const response = await $fetch<Blob>(`/applications/${arId}/file/${fileId}?fileName=${name}`, {
      ...globalFetchOptions(),
      method: 'get',
    });
    downLoadBlob(response, name);

    return '';
  };

  const getFileSrc = async (arId: string, fileId: string, name: string) => {
    return $fetch<Blob>(`/applications/${arId}/file/${fileId}?fileName=${name}`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const refetchComments = ref(false);
  const setRefetchComments = () => {
    refetchComments.value = !refetchComments.value;
  };

  const showConfirmExcludedModel = ref(false);
  /** True from the moment the modal is requested — `showConfirmExcludedModel` only turns on after the blur-all
   * timers below, so a handler that runs right after the request can't read it yet. */
  const excludedModelRequested = ref(false);
  const setConfirmExcludedModel = (show: boolean) => {
    excludedModelRequested.value = show;
    if (show) {
      setTimeout(() => {
        blurAllFields();
        setTimeout(() => {
          showConfirmExcludedModel.value = show;
        }, 50);
      }, 50);
    } else {
      showConfirmExcludedModel.value = false;
    }
  };
  const blurAllFields = () => {
    // Assume you have a unique id for your form as 'myForm'
    const form = document.getElementById('myForm');
    if (form) {
      const elements = form.querySelectorAll('input, textarea, select');

      for (const element of elements) {
        // @ts-ignore
        element.blur();
      }
    }
  };
  const exculdedField = ref<Fields>();

  const setExculdedField = (field: Fields) => {
    if (!showConfirmExcludedModel.value) {
      exculdedField.value = field;
    }
  };
  const nodeExcludedField = ref<FormKitNode>();
  const setNodeExcludedField = (node: FormKitNode | undefined) => {
    if (!showConfirmExcludedModel.value) {
      nodeExcludedField.value = node;
    }
  };
  const clearNodeExcludedFieldValue = () => {
    // @ts-ignore
    nodeExcludedField.value.reset();
  };
  const editExcludedFields = ref<{ [key: string]: boolean }>({});
  const setEditExcludedFields = (field: string, value: boolean) => {
    editExcludedFields.value[field] = value;
  };
  const clearEditExcludedFields = () => {
    editExcludedFields.value = {};
  };

  const editButtonDisabled = ref(false);
  const setEditButtonDisabled = (value: boolean) => {
    editButtonDisabled.value = value;
  };

  // Warns the admin before an edit turns off an eligibility flag (e.g. Homeless, Domestic Violence) —
  // separate state from the Fund/AssistanceType `fieldConfigs` exclusion flow above, since it's a
  // different concern (an eligibility flag, not a fund's field display rules) triggered on different fields.
  const showConfirmEligibilityModel = ref(false);
  const setConfirmEligibilityModel = (show: boolean) => {
    if (show) {
      setTimeout(() => {
        blurAllFields();
        setTimeout(() => {
          showConfirmEligibilityModel.value = show;
        }, 50);
      }, 50);
    } else {
      showConfirmEligibilityModel.value = false;
    }
  };
  const eligibilityWarningLabel = ref('');
  const setEligibilityWarningLabel = (label: string) => {
    if (!showConfirmEligibilityModel.value) {
      eligibilityWarningLabel.value = label;
    }
  };
  /** Name(s) of the Custom Form(s) conditioned on this eligibility flag — shown in the warning's body text */
  const eligibilityWarningFormName = ref('');
  const setEligibilityWarningFormName = (formName: string) => {
    if (!showConfirmEligibilityModel.value) {
      eligibilityWarningFormName.value = formName;
    }
  };
  const nodeEligibilityField = ref<FormKitNode>();
  const setNodeEligibilityField = (node: FormKitNode | undefined) => {
    if (!showConfirmEligibilityModel.value) {
      nodeEligibilityField.value = node;
    }
  };
  const clearNodeEligibilityFieldValue = () => {
    // @ts-ignore
    nodeEligibilityField.value.reset();
  };
  const setShowOnPrintToFalse = () => {
    showOnPrint.value.forEach((_, i) => {
      if (showOnPrint.value[i].show !== undefined) showOnPrint.value[i].show = false;
    });
  };
  const changeShowOnPrint = (stepId: string, value: boolean) => {
    showOnPrint.value = showOnPrint.value.map((item) => {
      if (item.stepId === stepId) {
        item.show = value;
      }
      return item;
    });
  };

  const getSupportingDocuments = (id: string) => {
    return $fetch<{ success: boolean; data: any }>(`/applications/${id}/documents`, {
      ...globalFetchOptions(),
      method: 'GET',
    });
  };

  const getRequiredDocuments = (assistanceRequestId: string) => {
    return $fetch<{ success: boolean; data: any }>(`/payee/${assistanceRequestId}/required-documents`, {
      ...globalFetchOptions(),
      method: 'GET',
    });
  };

  /** Replaces the requisite document's files (`documents`) — send the full list to keep the existing ones */
  const saveRequiredDocuments = (assistanceRequestId: string, formData: any) => {
    return $fetch<SavePersonalDetails>(`/payee/${assistanceRequestId}/required-documents`, {
      ...globalFetchOptions(),
      method: 'POST',
      body: formData,
      retry: false,
    });
  };

  const editRequiredDocuments = (assistanceRequestId: string) => {
    return useMutation((formData: any) => saveRequiredDocuments(assistanceRequestId, formData));
  };

  const deleteRequiredDocument = (assistanceRequestId: string) => {
    return useMutation((formData: { requiredDocId: string; docUrl: string }) => {
      return $fetch<{ success: boolean }>(
        `/payee/${assistanceRequestId}/required-documents/${formData.requiredDocId}/${formData.docUrl}`,
        {
          ...globalFetchOptions(),
          method: 'DELETE',
          retry: false,
        }
      );
    });
  };

  const payeeSendEmail = (assistanceRequestId: string) => {
    return useMutation((formData: any) => {
      return $fetch<SavePersonalDetails>(`/payee/${assistanceRequestId}/required-documents/email/send`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: formData,
        retry: false,
      });
    });
  };

  const payeePreviewEmail = (assistanceRequestId: string) => {
    return useMutation((formData: any) => {
      return $fetch<SavePersonalDetails>(`/payee/${assistanceRequestId}/required-documents/email/preview`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: formData,
        retry: false,
      });
    });
  };

  const getSupportingDocumentsById = (id: string, documentId: string) => {
    return $fetch<{ success: boolean; data: any }>(`/applications/${id}/documents/${documentId}`, {
      ...globalFetchOptions(),
      method: 'GET',
    });
  };

  const getSupportingDocumentsByIdSas = (id: string, documentId: string, fileName: string) => {
    return $fetch<{ success: boolean; data: any }>(
      `/applications/${id}/documents/${documentId}/sas?filename=${encodeURIComponent(sanitizeFileName(fileName))}`,
      {
        ...globalFetchOptions(),
        method: 'GET',
      }
    );
  };

  const deleteSupportingDocuments = (id: string) => {
    return useMutation((formData: { supportingDocumentsId: string }) => {
      return $fetch(`/applications/${id}/documents/${formData.supportingDocumentsId}`, {
        ...globalFetchOptions(),
        method: 'DELETE',
        retry: false,
      });
    });
  };

  const addSupportingDocumentsFile = (id: string) => {
    return useMutation((formData: { title: string; formFile: any }) => {
      const title = formData.title;
      const file = formData.formFile[0].file as File;
      const fileFormData = new FormData();
      fileFormData.append('formFile', file);
      const url = `/applications/${id}/documents?title=${encodeURIComponent(title)}`;
      return $fetch<SavePersonalDetails>(url, {
        ...globalFetchOptions(),
        method: 'POST',
        body: fileFormData,
        retry: false,
      });
    });
  };

  const downloadSupportingDocumentsFile = async (fsId: string, documentId: string, name: string) => {
    const response = await $fetch<Blob>(`/applications/${fsId}/documents/${documentId}`, {
      ...globalFetchOptions(),
      method: 'get',
    });
    downLoadBlob(response, name);
    return '';
  };

  const getChatNotification = () => {
    return $fetch<{
      success: boolean;
      data: { applicantName: string; applicationId: string; applicationNumber: number }[];
    }>(`/applications/chat/notification`, {
      ...globalFetchOptions(),
      method: 'get',
      retry: 0,
    });
  };
  const scrollToChat = ref(false);
  const setScrollToChat = () => {
    scrollToChat.value = !scrollToChat.value;
  };

  const switchServiceArea = (id: string, updateStatus = false) => {
    return useMutation((serviceAreaId: string) =>
      $fetch<{ success: boolean }>(`/applications/${id}/transfer-service-area/${serviceAreaId}`, {
        ...globalFetchOptions(),
        method: 'PUT',
        retry: false,
        body: { updateStatus },
      })
    );
  };

  const bypassCommunityService = (id: string) => {
    return useMutation((body: any) =>
      $fetch<{ success: boolean }>(`/applications/${body.id}/integration/bypass`, {
        ...globalFetchOptions(),
        method: 'POST',
        body,
      })
    );
  };

  const checkZipCode = () => {
    return useMutation((zipCode: string) =>
      $fetch<{
        success: boolean;
        data: [{ id: string; canAdd: boolean; active: boolean; hasActiveNonEmptyFund: boolean; hasAdminAccess: boolean }];
      }>(`/service-areas/${zipCode}/get-service-area-validation`, {
        ...globalFetchOptions(),
        method: 'GET',
        retry: false,
      })
    );
  };

  const uploadConsent = (applicationId: string) => {
    return useMutation((formData: any) =>
      $fetch<{
        success: boolean;
        data: any;
      }>(`/applications/${applicationId}/consent`, {
        ...globalFetchOptions(),
        method: 'POST',
        retry: false,
        body: formData,
      })
    );
  };

  const deleteConsent = (applicationId: string) => {
    return useMutation((formData: { consentId: string }) =>
      $fetch<{ success: boolean }>(`/applications/${applicationId}/consent/${formData.consentId}`, {
        ...globalFetchOptions(),
        method: 'DELETE',
        retry: false,
      })
    );
  };

  const getApplicantTokenById = (applicantId: string, serviceAreaId: string) => {
    return useMutation((formData: any) =>
      $fetch<{
        success: boolean;
        data: any;
      }>(`/applicants/${applicantId}/regenerate-admin-token/${serviceAreaId}`, {
        ...globalFetchOptions(),
        method: 'GET',
        retry: false,
      })
    );
  };

  const detailsByEmail = () => {
    return useMutation((applicantEmail: string) =>
      $fetch<{
        success: boolean;
        data: any;
      }>(`/applicants/details`, {
        ...globalFetchOptions(),
        method: 'POST',
        body: {
          email: applicantEmail,
        },
        retry: false,
      })
    );
  };

  const createApplicant = () => {
    return useMutation((formData: any) =>
      $fetch<{
        success: boolean;
        data: any;
      }>(`/applicants`, {
        ...globalFetchOptions(),
        method: 'POST',
        retry: false,
        body: formData,
      })
    );
  };

  const getTokenById = (id: string, serviceAreaId: string) => {
    return useMutation((formData: any) =>
      $fetch<{
        success: boolean;
        data: any;
      }>(`/applicants/${id}/regenerate-admin-token/${serviceAreaId}`, {
        ...globalFetchOptions(),
        method: 'GET',
        retry: false,
      })
    );
  };

  const canCreate = () => {
    const fetcher = async () =>
      $fetch<{ success: boolean; data: { result: ServiceAreaFilterView[] }; count: number }>(`/applications/can-create`, {
        ...globalFetchOptions(),
        method: 'get',
      });

    return useQuery({ queryKey: ['user-can-create'], queryFn: fetcher, ...globalUseQueryOptions() });
  };

  const translateText = () => {
    return useMutation((formData: any) =>
      $fetch<{
        success: boolean;
        data: any;
      }>(`/translations/translate`, {
        ...globalFetchOptions(),
        method: 'POST',
        retry: false,
        body: formData,
      })
    );
  };

  const categoryCommentHistory = ref<string[]>([]);
  const setCategoryCommentHistory = (value: string[]) => {
    categoryCommentHistory.value = value;
  };

  const checkDuplicateAddress = (assistanceRequestId: string, applicantId: string) => {
    return $fetch<{ success: boolean; data: any }>(
      `/assistance-requests/${assistanceRequestId}/check-for-duplicate-address/${applicantId}`,
      {
        ...globalFetchOptions(),
        method: 'GET',
      }
    );
  };

  const getArAffectedExpirationDate = () => {
    return $fetch<{ success: boolean; data: string }>(`/assistance-requests/total-affected-food-assistance`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  const getArAffectedExpirationDateDisaster = () => {
    return $fetch<{ success: boolean; data: string }>(`/assistance-requests/total-affected-disaster-assistance`, {
      ...globalFetchOptions(),
      method: 'GET',
      retry: false,
    });
  };

  // Generic, assistance-type-id keyed version used by the distribution-types module.
  // Replaces the food/disaster-specific endpoints above.
  const getArAffectedExpirationDateByType = (assistanceTypeId: string) => {
    return $fetch<{ success: boolean; data: string }>(
      `/assistance-types/${assistanceTypeId}/total-affected-ars-by-configuration`,
      {
        ...globalFetchOptions(),
        method: 'GET',
        retry: false,
      }
    );
  };

  const getArAffectedNextEligibleDate = () => {
    return $fetch<{ success: boolean; data: string }>(
      `/assistance-requests/total-affected-food-assistance-next-eligible-date`,
      {
        ...globalFetchOptions(),
        method: 'GET',
        retry: false,
      }
    );
  };

  const getAvailabilitySlots = (arId: string, pantryId: string, appointmentId?: string) => {
    console.log(pantryId);
    if (pantryId === undefined) {
      return { success: false, data: [] };
    }
    return $fetch<{ success: boolean; data: any[] }>(
      `/assistance-requests/${arId}/distributions/${pantryId}/available-slots${appointmentId ? `/${appointmentId}` : ''}`,
      {
        ...globalFetchOptions(),
        method: 'GET',
        retry: false,
      }
    );
  };

  const currentApp = ref<AssistanceRequest>();

  const getConsentText = (id: string) => {
    return $fetch<{ success: boolean; data: unknown }>(`/applications/${id}/consent-texts`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  const getFplData = (id: string) => {
    return $fetch<{ success: boolean; data: unknown }>(`/applications/${id}/fpl-account-details`, {
      ...globalFetchOptions(),
      method: 'get',
    });
  };

  return {
    getById,
    edit,
    uploadDocument,
    getDocument,
    getHistory,
    getAppointmentHistory,
    addComment,
    getComments,
    getChatHistory,
    addChatText,
    addChatFile,
    deleteChatMessage,
    downloadChatFile,
    updateStatus,
    statusToPermission,
    getValidNextStatuses,
    getDisplayStringForStatusUpdateButtons,
    chatNotifyInit,
    getUnreadChatMessage,
    getConsentById,
    getCustomFormFiles,
    getSignature,
    refetchComments,
    setRefetchComments,
    showConfirmExcludedModel,
    excludedModelRequested,
    setConfirmExcludedModel,
    exculdedField,
    setExculdedField,
    nodeExcludedField,
    setNodeExcludedField,
    clearNodeExcludedFieldValue,
    editExcludedFields,
    setEditExcludedFields,
    clearEditExcludedFields,
    showOnPrint,
    editButtonDisabled,
    setEditButtonDisabled,
    showConfirmEligibilityModel,
    setConfirmEligibilityModel,
    eligibilityWarningLabel,
    setEligibilityWarningLabel,
    eligibilityWarningFormName,
    setEligibilityWarningFormName,
    nodeEligibilityField,
    setNodeEligibilityField,
    clearNodeEligibilityFieldValue,
    setShowOnPrintToFalse,
    getSupportingDocuments,
    getSupportingDocumentsById,
    deleteSupportingDocuments,
    addSupportingDocumentsFile,
    downloadSupportingDocumentsFile,
    getChatNotification,
    scrollToChat,
    setScrollToChat,
    getEmailReceived,
    resendEmail,
    getSmsReceived,
    resendSms,
    getVideoSessions,
    switchServiceArea,
    getPayeeEmailSent,
    resendPayeeEmail,
    getPayeeInformation,
    getPayeeConsentById,
    getAppointment,
    checkIntegrationEnabled,
    integrationEnabled,
    setIntegrationEnabled,
    getRequiredDocuments,
    saveRequiredDocuments,
    editRequiredDocuments,
    deleteRequiredDocument,
    payeeSendEmail,
    payeePreviewEmail,
    getVideoRecordingUrl,
    checkZipCode,
    uploadConsent,
    deleteConsent,
    getApplicantTokenById,
    detailsByEmail,
    createApplicant,
    getTokenById,
    canCreate,
    translateText,
    changeShowOnPrint,
    categoryCommentHistory,
    setCategoryCommentHistory,
    updateChatFlag,
    updateArFlag,
    getFileSrc,
    checkDuplicateAddress,
    getArAffectedExpirationDate,
    currentApp,
    getAvailabilitySlots,
    getSasDocument,
    getSupportingDocumentsByIdSas,
    getArAffectedNextEligibleDate,
    getArAffectedExpirationDateDisaster,
    getArAffectedExpirationDateByType,
    getConsentText,
    checkActiveWellsky,
    activeWellsky,
    setActiveWellsky,
    bypassCommunityService,
    wellskyActive,
    setWellskyActive,
    getFplData,
  };
});
